import { useEffect, useState } from "react";
import axios from "axios";
import { Send, Loader2, MailCheck, Users as UsersIcon, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatError = (d) =>
    !d
        ? "Something went wrong."
        : typeof d === "string"
            ? d
            : Array.isArray(d)
                ? d.map((e) => e?.msg || "").filter(Boolean).join(" ")
                : d?.msg || "Something went wrong.";

// One-click broadcast to every user with stay_informed=true. Backed by
// POST /api/admin/newsletter/send which fans out through Resend using an
// asyncio semaphore so free-tier rate limits stay comfortable.
export default function NewsletterPanel({ token }) {
    const [form, setForm] = useState({
        subject: "",
        intro: "",
        body: "",
        cta_label: "Read on EquityNation.ca",
        cta_url: "https://equitynation.ca",
    });
    const [subscriberCount, setSubscriberCount] = useState(null);
    const [sending, setSending] = useState(false);
    const [testing, setTesting] = useState(false);
    const [result, setResult] = useState(null);
    const [dispatches, setDispatches] = useState([]);
    const [prefilled, setPrefilled] = useState(false);

    const headers = { Authorization: `Bearer ${token}` };
    const update = (field) => (e) =>
        setForm((f) => ({ ...f, [field]: e.target.value }));

    // Pick up a "Compose dispatch about this article" prefill from the
    // Articles tab. Runs once on mount; consumes the sessionStorage entry so
    // navigating away and back doesn't re-fill over admin edits.
    useEffect(() => {
        try {
            const raw = sessionStorage.getItem("newsletter_prefill");
            if (!raw) return;
            const pf = JSON.parse(raw);
            sessionStorage.removeItem("newsletter_prefill");
            setForm((f) => ({ ...f, ...pf }));
            setPrefilled(true);
            // Auto-hide the banner after a few seconds so it doesn't linger.
            setTimeout(() => setPrefilled(false), 6000);
        } catch {
            // corrupt JSON — ignore.
        }
    }, []);

    const loadDispatches = async () => {
        try {
            const r = await axios.get(
                `${API}/admin/newsletter/dispatches?limit=10`,
                { headers },
            );
            setDispatches(r.data || []);
        } catch {
            // non-fatal
        }
    };

    // Preflight the subscriber count so admins know the size of the send.
    useEffect(() => {
        let cancel = false;
        (async () => {
            try {
                const r = await axios.get(`${API}/admin/users`, { headers });
                if (cancel) return;
                const subs = (r.data || []).filter(
                    (u) => u.stay_informed !== false,
                ).length;
                setSubscriberCount(subs);
            } catch {
                // preflight failure is non-fatal
            }
            if (!cancel) loadDispatches();
        })();
        return () => {
            cancel = true;
        };
    }, [token]);

    const sendTest = async () => {
        setResult(null);
        if (!form.subject.trim() || !form.intro.trim() || !form.body.trim()) {
            setResult({
                type: "error",
                msg: "Fill in subject, intro, and body before sending a test.",
            });
            return;
        }
        setTesting(true);
        try {
            const r = await axios.post(
                `${API}/admin/newsletter/test`,
                form,
                { headers },
            );
            setResult({
                type: r.data?.skipped ? "warning" : "success",
                msg: r.data?.skipped
                    ? `Preview only — ${r.data.message || "RESEND_API_KEY not configured."}`
                    : `Test sent to ${r.data.sent_to}. Check your inbox.`,
            });
        } catch (err) {
            setResult({
                type: "error",
                msg: formatError(err.response?.data?.detail),
            });
        } finally {
            setTesting(false);
        }
    };

    const send = async (e) => {
        e.preventDefault();
        setResult(null);
        if (!form.subject.trim() || !form.intro.trim() || !form.body.trim()) {
            setResult({
                type: "error",
                msg: "Subject, intro, and body are all required.",
            });
            return;
        }
        if (
            !window.confirm(
                `Send this dispatch to ${subscriberCount ?? "all"} Stay Informed members? This can't be undone.`,
            )
        )
            return;
        setSending(true);
        try {
            const r = await axios.post(
                `${API}/admin/newsletter/send`,
                form,
                { headers },
            );
            setResult({
                type: r.data?.skipped > 0 ? "warning" : "success",
                msg:
                    r.data?.skipped > 0
                        ? `Preview complete — would send to ${r.data.total}. ${r.data.message || "RESEND_API_KEY not configured."}`
                        : `Sent to ${r.data.sent} of ${r.data.total}. Failed: ${r.data.failed}.`,
                data: r.data,
            });
            // Refresh the dispatch log so the new entry shows immediately.
            await loadDispatches();
        } catch (err) {
            setResult({
                type: "error",
                msg: formatError(err.response?.data?.detail),
            });
        } finally {
            setSending(false);
        }
    };

    return (
        <section
            data-testid="newsletter-panel"
            className="mt-12 border-t border-[color:var(--charcoal)]/20 pt-10"
        >
            <div className="flex items-center gap-3 mb-6 flex-wrap">
                <MailCheck size={18} strokeWidth={1.5} />
                <h2 className="font-display text-xl md:text-2xl">
                    Notify Stay Informed members
                </h2>
                {subscriberCount !== null && (
                    <span
                        data-testid="newsletter-subscriber-count"
                        className="inline-flex items-center gap-1.5 border border-[color:var(--charcoal)] px-2 py-0.5 font-mono font-semibold text-[12px] uppercase tracking-[0.22em]"
                    >
                        <UsersIcon size={12} strokeWidth={1.7} />
                        {subscriberCount} subscriber
                        {subscriberCount === 1 ? "" : "s"}
                    </span>
                )}
            </div>

            <p className="font-body text-[color:var(--charcoal)]/85 mb-6 max-w-[62ch]">
                Compose a plain-language dispatch and broadcast it to every
                member with the <em>Stay Informed</em> preference on. Emails go
                out through Resend — configure <code>RESEND_API_KEY</code> in
                <code> /app/backend/.env</code> to light up live sending.
            </p>

            {prefilled && (
                <div
                    data-testid="newsletter-prefilled-banner"
                    className="mb-6 inline-flex items-center gap-2 border border-[color:var(--charcoal)] bg-[color:var(--cloud)] px-3 py-2 font-mono text-[12px] uppercase tracking-[0.22em]"
                >
                    <Mail size={12} strokeWidth={1.7} />
                    Draft prefilled from article — review and edit before
                    sending
                </div>
            )}

            <form
                onSubmit={send}
                data-testid="newsletter-form"
                className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4"
            >
                <NewsletterField
                    label="Subject"
                    testId="newsletter-subject"
                    value={form.subject}
                    onChange={update("subject")}
                    maxLength={180}
                    placeholder="A new chapter is live — read it now."
                />
                <NewsletterField
                    label="CTA button label"
                    testId="newsletter-cta-label"
                    value={form.cta_label}
                    onChange={update("cta_label")}
                    maxLength={60}
                />
                <NewsletterField
                    label="CTA link"
                    testId="newsletter-cta-url"
                    value={form.cta_url}
                    onChange={update("cta_url")}
                    maxLength={400}
                    type="url"
                />
                <div />

                <label className="block md:col-span-2">
                    <span className="block font-mono font-semibold text-[12px] uppercase tracking-[0.22em] text-[color:var(--slate)] mb-1.5">
                        Intro (single paragraph, shown before the body)
                    </span>
                    <textarea
                        value={form.intro}
                        onChange={update("intro")}
                        data-testid="newsletter-intro"
                        rows={3}
                        maxLength={400}
                        className="w-full bg-transparent border border-[color:var(--charcoal)]/25 focus:border-[color:var(--charcoal)] outline-none px-3 py-2 font-body text-base resize-y"
                    />
                </label>

                <label className="block md:col-span-2">
                    <span className="block font-mono font-semibold text-[12px] uppercase tracking-[0.22em] text-[color:var(--slate)] mb-1.5">
                        Body (paragraphs separated by blank lines)
                    </span>
                    <textarea
                        value={form.body}
                        onChange={update("body")}
                        data-testid="newsletter-body"
                        rows={8}
                        maxLength={8000}
                        className="w-full bg-transparent border border-[color:var(--charcoal)]/25 focus:border-[color:var(--charcoal)] outline-none px-3 py-2 font-body text-base resize-y"
                    />
                </label>

                {result && (
                    <div
                        data-testid="newsletter-status"
                        className={`md:col-span-2 p-3 border font-body text-sm ${
                            result.type === "success"
                                ? "border-[color:var(--charcoal)] bg-[color:var(--charcoal)]/5"
                                : result.type === "warning"
                                    ? "border-[color:var(--charcoal)]/60"
                                    : "border-red-800 text-red-900"
                        }`}
                    >
                        {result.msg}
                    </div>
                )}

                <div className="md:col-span-2 flex gap-3 flex-wrap">
                    <button
                        type="submit"
                        disabled={sending || testing}
                        data-testid="newsletter-send"
                        className="pill-btn disabled:opacity-60"
                        style={{ padding: "0.7rem 1.3rem" }}
                    >
                        {sending ? (
                            <>
                                <Loader2 size={14} className="animate-spin" />
                                Sending
                            </>
                        ) : (
                            <>
                                <Send size={14} strokeWidth={1.6} />
                                Send dispatch
                            </>
                        )}
                    </button>
                    <button
                        type="button"
                        onClick={sendTest}
                        disabled={sending || testing}
                        data-testid="newsletter-send-test"
                        className="pill-btn ghost disabled:opacity-60"
                        style={{ padding: "0.7rem 1.3rem" }}
                        title="Preview the rendered email — sent only to your admin address"
                    >
                        {testing ? (
                            <>
                                <Loader2 size={14} className="animate-spin" />
                                Sending test
                            </>
                        ) : (
                            <>
                                <Mail size={14} strokeWidth={1.6} />
                                Send test to me only
                            </>
                        )}
                    </button>
                </div>
            </form>

            {/* Send log — receipts of every dispatch triggered from here. */}
            <div
                data-testid="newsletter-dispatch-log"
                className="mt-10 pt-8 border-t border-[color:var(--charcoal)]/15"
            >
                <div className="flex items-center gap-3 mb-4">
                    <span className="font-mono font-semibold text-[12px] uppercase tracking-[0.24em] text-[color:var(--slate)]">
                        Send log
                    </span>
                    {dispatches.length > 0 && (
                        <span
                            data-testid="newsletter-dispatch-last"
                            className="font-mono text-[12px] uppercase tracking-[0.22em] text-[color:var(--charcoal)]"
                        >
                            Last dispatch · {dispatches[0].sent} sent ·{" "}
                            {dispatches[0].sent_at?.slice(0, 10)}
                        </span>
                    )}
                </div>
                {dispatches.length === 0 ? (
                    <p className="font-body text-sm text-[color:var(--charcoal)]/70 italic">
                        No dispatches yet — the first Send Dispatch will show
                        up here with delivery counts.
                    </p>
                ) : (
                    <ul className="divide-y divide-[color:var(--charcoal)]/15">
                        {dispatches.map((d) => (
                            <li
                                key={d.id}
                                data-testid={`newsletter-dispatch-${d.id}`}
                                className="py-3 flex items-baseline justify-between gap-4 flex-wrap"
                            >
                                <div className="flex items-baseline gap-3 flex-wrap">
                                    <span className="font-display italic text-base">
                                        {d.subject || "(no subject)"}
                                    </span>
                                    <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[color:var(--slate)]">
                                        {d.sent_at?.slice(0, 10)}
                                    </span>
                                    {d.status === "preview" && (
                                        <span className="inline-block px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.22em] border border-[color:var(--charcoal)]/40 text-[color:var(--slate)]">
                                            Preview only
                                        </span>
                                    )}
                                </div>
                                <span className="font-mono text-[12px] uppercase tracking-[0.22em] text-[color:var(--charcoal)]">
                                    {d.sent}/{d.total} sent
                                    {d.failed > 0 && ` · ${d.failed} failed`}
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </section>
    );
}

const NewsletterField = ({
    label,
    testId,
    value,
    onChange,
    type = "text",
    maxLength,
    placeholder,
}) => (
    <label className="block">
        <span className="block font-mono font-semibold text-[12px] uppercase tracking-[0.22em] text-[color:var(--slate)] mb-1.5">
            {label}
        </span>
        <input
            type={type}
            value={value}
            onChange={onChange}
            data-testid={testId}
            maxLength={maxLength}
            placeholder={placeholder}
            className="w-full bg-transparent border border-[color:var(--charcoal)]/25 focus:border-[color:var(--charcoal)] outline-none px-3 py-2 font-body text-base"
        />
    </label>
);
